import type { PostalRouteRow } from "../lib/admin-api";

export interface ManualAddressFields {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  addressLine5: string;
  addressLine6: string;
  townCity: string;
  county: string;
  postcode: string;
  bfpoNumber: string;
  country: string;
  routingReference: string;
  routingReferenceVerified: boolean;
  sourceDocument: string;
  notes: string;
}

export const EMPTY_MANUAL_ADDRESS: ManualAddressFields = {
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  addressLine4: "",
  addressLine5: "",
  addressLine6: "",
  townCity: "",
  county: "",
  postcode: "",
  bfpoNumber: "",
  country: "",
  routingReference: "",
  routingReferenceVerified: false,
  sourceDocument: "Manually entered in admin",
  notes: "",
};

export function postalRouteToManualAddress(route: PostalRouteRow): ManualAddressFields {
  const organisation = [route.address_line2, route.address_line3]
    .map((line) => line?.trim())
    .filter(Boolean)
    .join(" / ");
  return {
    addressLine1: route.address_line1 ?? "",
    addressLine2: organisation,
    addressLine3: "",
    addressLine4: route.address_line4 ?? "",
    addressLine5: route.address_line5 ?? "",
    addressLine6: route.address_line6 ?? "",
    townCity: route.town_city ?? "",
    county: route.county ?? "",
    postcode: route.postcode ?? "",
    bfpoNumber: route.bfpo_number ?? "",
    country: route.country || "United Kingdom",
    routingReference: route.routing_reference ?? "",
    routingReferenceVerified: route.routing_reference_verified === 1,
    sourceDocument: route.source_document ?? "Manually entered in admin",
    notes: route.notes ?? "",
  };
}

export function validateManualAddress(fields: ManualAddressFields): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!fields.addressLine1.trim()) errors.addressLine1 = "Enter the recipient or first address line.";
  if (!fields.addressLine4.trim()) errors.addressLine4 = "Enter the first physical address line.";
  if (!fields.townCity.trim()) errors.townCity = "Enter the town or city.";
  if (!fields.postcode.trim()) errors.postcode = "Enter the postcode.";
  if (!fields.country.trim()) errors.country = "Enter the country.";
  return errors;
}

export function formatPostalRoute(route: PostalRouteRow): string[] {
  const locality = [route.town_city, route.county].filter(Boolean).join(", ");
  return [
    route.address_line1,
    route.address_line2,
    route.address_line3,
    route.address_line4,
    route.address_line5,
    route.address_line6,
    locality || null,
    route.postcode,
    route.bfpo_number ? `BFPO ${route.bfpo_number}` : null,
    route.country && route.country !== "United Kingdom" ? route.country : null,
  ].filter((line): line is string => Boolean(line?.trim()));
}

export function formatManualAddress(fields: ManualAddressFields): string[] {
  const locality = [fields.townCity, fields.county].filter((value) => value.trim()).join(", ");
  return [
    fields.addressLine1,
    fields.addressLine2,
    fields.addressLine3,
    fields.addressLine4,
    fields.addressLine5,
    fields.addressLine6,
    locality,
    fields.postcode,
    fields.bfpoNumber ? `BFPO ${fields.bfpoNumber}` : "",
    fields.country !== "United Kingdom" ? fields.country : "",
  ].filter((line) => line.trim());
}

/** Mirrors the compact, label-visible order used by the carrier adapters. */
export function formatCarrierLabelAddress(fields: ManualAddressFields): string[] {
  const organisation = [fields.addressLine2, fields.addressLine3]
    .map((line) => line.trim())
    .filter(Boolean)
    .join(" / ");
  const postTown = [fields.townCity, fields.county].filter((value) => value.trim()).join(", ");
  return [
    fields.addressLine1,
    organisation,
    fields.addressLine4,
    fields.addressLine5,
    fields.addressLine6,
    postTown,
    fields.postcode,
    fields.bfpoNumber ? `BFPO ${fields.bfpoNumber}` : "",
    fields.country !== "United Kingdom" ? fields.country : "",
  ].filter((line) => line.trim());
}
